import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow } from "@/components/Motion";
import { apiFetch } from "@/lib/brand";

const PATH_INFO = {
    skeptic: {
        eyebrow: "Portal One — AI Skeptic",
        lines: ["Let's talk", "about AI."],
        copy: "Your concerns are welcome here — no judgment, no pressure to change your mind. Tell us what worries you and what you want the future of creative work to look like.",
    },
    curious: {
        eyebrow: "Portal Two — The Curious Creative",
        lines: ["Still figuring", "it out? Good."],
        copy: "Curious about AI, creative development, or building a brand? Explore the possibilities without pressure to choose a side.",
    },
    builder: {
        eyebrow: "Portal Three — The Tool User",
        lines: ["Let's build", "something."],
        copy: "Already using AI or other creative tools? Let's turn your ideas into a recognizable identity and a practical plan.",
    },
    unsure: {
        eyebrow: "No Wrong Answers",
        lines: ["Start", "anywhere."],
        copy: "You don't need to know your lane yet. Answer a few honest questions and we'll help you find a starting point.",
    },
};

const DIRECTIONS = [
    { id: "artist", label: "Artist" },
    { id: "singer-rapper", label: "Singer or Rapper" },
    { id: "producer", label: "Producer" },
    { id: "dj", label: "DJ" },
    { id: "streamer", label: "Streamer" },
    { id: "ugc-creator", label: "UGC Creator" },
    { id: "actor", label: "Actor" },
    { id: "filmmaker", label: "Filmmaker" },
    { id: "aspiring-creative", label: "Aspiring Creative" },
    { id: "still-exploring", label: "Still Exploring" },
];

const EXPERIENCE = [
    { id: "just-started", label: "Just getting started" },
    { id: "ideas-need-direction", label: "Have ideas but need direction" },
    { id: "already-creating", label: "Already creating content" },
    { id: "established-growing", label: "Established identity, want to grow" },
    { id: "exploring-ai", label: "Exploring AI and creative tools" },
];

const NEEDS = [
    { id: "creative-direction", label: "Creative direction" },
    { id: "branding", label: "Branding" },
    { id: "content-planning", label: "Content planning" },
    { id: "music-development", label: "Music development" },
    { id: "audience-development", label: "Audience development" },
    { id: "ai-education", label: "AI education" },
    { id: "accountability-structure", label: "Accountability & structure" },
    { id: "business-development", label: "Business development" },
    { id: "other", label: "Other" },
];

function Chip({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={active}
            className={`rounded-full px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                active
                    ? "bg-purp text-obsidian border-purp font-bold"
                    : "border-white/15 text-slate-400 hover:border-purp/50 hover:text-purp-neon"
            }`}
        >
            {children}
        </button>
    );
}

export default function Discover() {
    const [params] = useSearchParams();
    const path = PATH_INFO[params.get("path")] ? params.get("path") : "curious";
    const info = PATH_INFO[path];

    const [form, setForm] = useState({
        name: "", email: "", social: "", directions: [], experience: "",
        goals: "", needs: [], ai_perspective: "", marketing_consent: false,
    });
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const toggle = (key, id) =>
        setForm((f) => ({
            ...f,
            [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
        }));

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Tell us what to call you.";
        if (!form.email.trim()) e.email = "We need an email to respond.";
        if (form.directions.length === 0) e.directions = "Pick at least one — 'Still exploring' counts.";
        if (!form.experience) e.experience = "Pick the closest fit.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) {
            toast.error("A few fields need attention — check the highlights.");
            return;
        }
        setSending(true);
        try {
            await apiFetch("/discovery", {
                method: "POST",
                body: { ...form, name: form.name.trim(), email: form.email.trim(), social: form.social.trim(), path },
            });
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors";
    const err = (k) => errors[k] && <p className="mt-2 text-xs text-red-400" data-testid={`discover-error-${k}`}>{errors[k]}</p>;

    const sectionCls = "rounded-3xl bg-panel/75 backdrop-blur-md border border-white/10 p-6 md:p-8";
    const labelCls = "block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2";

    if (done) {
        return (
            <div data-testid="discover-page">
                <section className="min-h-[80vh] flex items-center pt-28 pb-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-radial-purp" />
                    <div className="relative max-w-2xl mx-auto px-5 md:px-8 text-center" data-testid="discover-success">
                        <CheckCircle2 className="w-14 h-14 text-volt mx-auto" />
                        <h1 className="mt-6 font-display font-extrabold uppercase text-5xl sm:text-6xl leading-[0.92]">
                            You&apos;re <span className="italic text-purp text-glow">in.</span>
                        </h1>
                        <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
                            Your information was received. Every submission is reviewed by a real person at PAN —
                            we&apos;ll use what you shared to understand your interests and determine the most
                            appropriate next step. Keep an eye on your inbox.
                        </p>
                        <div className="mt-9 flex flex-wrap justify-center gap-4">
                            <Link to="/pan-school" className="inline-flex items-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian" data-testid="discover-success-school">
                                Browse PAN School
                            </Link>
                            <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-purp/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-purp-neon" data-testid="discover-success-home">
                                Back Home
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div data-testid="discover-page">
            <PageHero eyebrow={info.eyebrow} titleLines={info.lines} copy={info.copy} />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="discover-form-section">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-10 border-l-2 border-purp pl-5">
                            Every creative has a different starting point. Tell us where you are and where you want
                            to go. We&apos;ll use that information to understand your interests and determine the most
                            appropriate next step.
                        </p>
                        <form onSubmit={submit} className="space-y-6" data-testid="discovery-form" noValidate>
                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-6">About You</h2>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="dc-name" className={labelCls}>Name or preferred name *</label>
                                        <input id="dc-name" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="discover-name" />
                                        {err("name")}
                                    </div>
                                    <div>
                                        <label htmlFor="dc-email" className={labelCls}>Email address *</label>
                                        <input id="dc-email" type="email" className={inputCls} placeholder="you@yourlane.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="discover-email" />
                                        {err("email")}
                                    </div>
                                </div>
                                <div className="mt-5">
                                    <label htmlFor="dc-social" className={labelCls}>Social username or profile link (optional)</label>
                                    <input id="dc-social" className={inputCls} placeholder="@yourhandle or https://…" value={form.social} onChange={(e) => setForm({ ...form, social: e.target.value })} data-testid="discover-social" />
                                </div>
                            </div>

                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-2">Your Creative Direction</h2>
                                <p className="text-xs text-slate-500 mb-5">Pick all that apply.</p>
                                <div className="flex flex-wrap gap-2" data-testid="discover-directions">
                                    {DIRECTIONS.map((d) => (
                                        <Chip key={d.id} active={form.directions.includes(d.id)} onClick={() => toggle("directions", d.id)} testid={`discover-dir-${d.id}`}>
                                            {d.label}
                                        </Chip>
                                    ))}
                                </div>
                                {err("directions")}
                            </div>

                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-2">Your Experience</h2>
                                <p className="text-xs text-slate-500 mb-5">Pick the closest fit.</p>
                                <div className="flex flex-wrap gap-2" data-testid="discover-experience">
                                    {EXPERIENCE.map((x) => (
                                        <Chip key={x.id} active={form.experience === x.id} onClick={() => setForm({ ...form, experience: x.id })} testid={`discover-exp-${x.id}`}>
                                            {x.label}
                                        </Chip>
                                    ))}
                                </div>
                                {err("experience")}
                            </div>

                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-6">Your Goals</h2>
                                <label htmlFor="dc-goals" className={labelCls}>What do you want to accomplish?</label>
                                <textarea id="dc-goals" rows={4} className={inputCls} placeholder="Be as specific or as rough as you need — half-formed ideas count." value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} maxLength={2000} data-testid="discover-goals" />
                            </div>

                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-2">What Do You Need Most?</h2>
                                <p className="text-xs text-slate-500 mb-5">Pick all that apply.</p>
                                <div className="flex flex-wrap gap-2" data-testid="discover-needs">
                                    {NEEDS.map((n) => (
                                        <Chip key={n.id} active={form.needs.includes(n.id)} onClick={() => toggle("needs", n.id)} testid={`discover-need-${n.id}`}>
                                            {n.label}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            <div className={sectionCls}>
                                <h2 className="font-display font-extrabold uppercase text-2xl mb-6">AI Perspective</h2>
                                <label htmlFor="dc-ai" className={labelCls}>How do you honestly feel about AI in creative work?</label>
                                <textarea id="dc-ai" rows={4} className={inputCls} placeholder="Skeptical, curious, all-in, against it — there's no wrong answer and no judgment here." value={form.ai_perspective} onChange={(e) => setForm({ ...form, ai_perspective: e.target.value })} maxLength={2000} data-testid="discover-ai" />
                                <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                                    PAN welcomes different perspectives on AI. Your answer helps us understand you —
                                    it&apos;s not a test.
                                </p>
                            </div>

                            <div className={sectionCls}>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="discover-consent-label">
                                    <input
                                        type="checkbox"
                                        checked={form.marketing_consent}
                                        onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                                        className="mt-0.5 w-4 h-4 rounded border-white/20 bg-obsidian accent-[#A855F7]"
                                        data-testid="discover-consent-checkbox"
                                    />
                                    <span className="text-xs text-slate-400 leading-relaxed">
                                        Optional: email me PAN updates and opportunities. Unchecked by default — you
                                        will not be added to any newsletter unless you check this box.
                                    </span>
                                </label>
                                <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                                    Privacy notice: the information you submit is used only to understand your
                                    interests, respond to you, and shape the right next step. We never sell your
                                    data. See the <Link to="/privacy" className="text-purp-neon underline underline-offset-2">Privacy Policy</Link>.
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="discover-submit"
                                className="inline-flex items-center gap-2 rounded-full bg-purp px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)] disabled:opacity-60"
                            >
                                {sending ? "Sending..." : "Submit"} <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
